import {useAsyncError, useLocation, useRouteError} from "react-router-dom";
import React from "react";
import {ApiError} from "../../../service/HttpService";
import AnimatedLink from "../../ui/motion/AnimatedLink";
import AnimatedButton from "../../ui/motion/AnimatedButton";
import {useOptionalUser} from "../../../context/UserContext";

interface ErrorContentProps {
    messageProps?: string
}

export default function ErrorContent(props: ErrorContentProps) {
    const {messageProps} = props;
    const error = useRouteError() as ApiError;
    const asyncError = useAsyncError() as ApiError;
    const location = useLocation();
    const userContext = useOptionalUser();

    const isAuthenticated = userContext?.isAuthenticated || false;
    const isActivationPage = location.pathname.startsWith('/aktywuj/');
    const isEn = location.pathname.startsWith('/en/') || location.pathname.startsWith('/en');

    let httpsStatus;
    if (error && error.httpsStatus) {
        httpsStatus = error.httpsStatus;
    } else if (asyncError && asyncError.httpsStatus) {
        httpsStatus = asyncError.httpsStatus;
    }

    let message = isEn ? "Sorry, something went wrong :(" : "Przepraszamy, ale coś poszło nie tak :(";
    if (error && error.message) {
        message = error.message;
    } else if (asyncError && asyncError.message) {
        message = asyncError.message;
    } else if (messageProps) {
        message = messageProps;
    }

    // Auto-refresh on chunk loading errors (stale cache after deployment)
    React.useEffect(function () {
        const isChunkError = message.includes('Loading chunk') || message.includes('ChunkLoadError');
        if (isChunkError) {
            const reloadKey = 'chunk-error-reload';
            const lastReload = sessionStorage.getItem(reloadKey);
            const now = Date.now();
            // Only auto-reload once per minute to avoid infinite loop
            if (!lastReload || now - parseInt(lastReload, 10) > 60000) {
                sessionStorage.setItem(reloadKey, now.toString());
                window.location.reload();
            }
        }
    }, [message]);

    return <>
        <div className='row align-items-center g-16'>
            <div className='col-12 col-md-6'>
                <div className='d-flex justify-content-center'>
                    <img
                        className='img-fluid rounded-3'
                        src='https://produkacja.s3.eu-central-1.amazonaws.com/web/common/error-code.png'
                        alt=''
                    />
                </div>
            </div>
            <div className='col-12 col-md-6'>
                <div className='mw-md-5xl mx-auto'>
                <span className='badge mb-4 bg-success-dark text-success shadow'>
                  Error
                </span>
                    <h1
                        className='font-heading mb-4 fs-5 text-white'
                        style={{letterSpacing: '-1px'}}
                    >
                        {isEn
                            ? `Oh no! ${httpsStatus ? `Error ${httpsStatus}` : 'An error occurred'}`
                            : `O nie! ${httpsStatus ? `Błąd ${httpsStatus}` : 'Wystąpił błąd'}`
                        }
                    </h1>
                    <p className='mb-12 fs-9 text-secondary-light'>
                        {message}
                    </p>
                    {!isEn && isAuthenticated && isActivationPage && (
                        <div className='mb-8 alert alert-warning bg-opacity-25 rounded-3'>
                            <p className='mb-0 fs-10'>
                                Jesteś już zalogowany. Możesz przejść do swoich produktów klikając{' '}
                                <AnimatedLink
                                    to="/produkt"
                                    className="text-warning fw-bold text-decoration-underline"
                                >
                                    tutaj
                                </AnimatedLink>.
                            </p>
                        </div>
                    )}
                    <div className='row g-4 g-md-6'>
                        <div className='col-12 col-md-auto'>
                            <AnimatedLink
                                className='btn btn-lg btn-success w-100 text-success-light shadow'
                                to={isEn ? '/en/webinar' : '/'}
                            >
                                {isEn ? 'Go back' : 'Wróć do strony głównej'}
                            </AnimatedLink>
                        </div>
                        <div className='col-12 col-md-auto'>
                            <AnimatedButton
                                className='btn btn-lg btn-dark-light w-100 text-success-light shadow'
                                onClick={() => window.location.reload()}
                            >
                                {isEn ? 'Try again' : 'Spróbuj ponownie'}
                            </AnimatedButton>
                        </div>
                        {!isEn && isAuthenticated && (
                            <div className='col-12 col-md-auto'>
                                <AnimatedLink
                                    className='btn btn-lg btn-warning w-100 text-warning-light shadow'
                                    to='/produkt'
                                >
                                    Przejdź do moich produktów
                                </AnimatedLink>
                            </div>
                        )}
                        {!isEn && !isAuthenticated && isActivationPage && (
                            <div className='col-12 col-md-auto'>
                                <AnimatedLink
                                    className='btn btn-lg btn-warning w-100 text-warning-light shadow'
                                    to='/logowanie'
                                >
                                    Zaloguj się do swojego konta
                                </AnimatedLink>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    </>
}